import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid
} from '@mui/material';
import apiClient from '../api/axios';

interface CompanyStat {
    company_name: string;
    contract_count: number;
    user_count: number;
    total_storage_size: number; // バイト単位
}

interface AdminStats {
    total_contracts: number;
    contracts_by_company: CompanyStat[];
}

// ファイルサイズを読みやすい形式に変換
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AdminDashboardPage: React.FC = () => {
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get<AdminStats>('/api/admin/stats');
                setStats(response.data);
                setError(null);
            } catch (err) {
                setError('統計データの取得に失敗しました。');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ padding: 2 }}>
                管理者ダッシュボード
            </Typography>
            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : stats ? (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                                    システム全体
                                </Typography>
                                <Typography variant="h5" component="div">
                                    総レコード数（契約数）
                                </Typography>
                                <Typography sx={{ mt: 1.5 }} variant="h3" color="text.primary">
                                    {stats.total_contracts}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            企業ごとの統計情報
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="company statistics table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>企業名</TableCell>
                                        <TableCell align="right">レコード数</TableCell>
                                        <TableCell align="right">アカウント数</TableCell>
                                        <TableCell align="right">ストレージ使用量</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats.contracts_by_company.map((row) => (
                                        <TableRow
                                            key={row.company_name}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {row.company_name}
                                            </TableCell>
                                            <TableCell align="right">{row.contract_count}</TableCell>
                                            <TableCell align="right">{row.user_count}</TableCell>
                                            <TableCell align="right">{formatFileSize(row.total_storage_size)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            ) : (
                <Typography>データがありません。</Typography>
            )}
        </Box>
    );
};

export default AdminDashboardPage;
